import React, { createContext, useCallback, useContext, useEffect, useState } from 'react'
import { QueueItem } from './index'
import Component from './Component'
import api from '../../lib/api'
import { SocketConnectorContext } from '../SocketConnector'
import { SimpleSnackbarContext } from '../SimpleSnackbar'
import { useMitt } from 'react-mitt'

const PROCESSING_TIMEOUT_MS = 30 * 60 * 1000

const getUploadErrorDetail = (error: any): string => {
  const status = error?.response?.status
  if (status === 413) {
    return 'File too large for direct upload (max ~32MB). Compress the video or contact support.'
  }
  if (status === 500) {
    return 'Server error during upload or conversion. Try again or use Reprocess Media on the story page.'
  }
  return error?.response?.data?.message || error?.message || 'Upload failed'
}

// @ts-ignore
export const QueueProcessorContext = createContext<{
  actions: {
    addProcessor: (item: Partial<QueueItem> & NonNullable<{ id: string; title: string }>) => Promise<void>
    open: (target: HTMLElement) => void
    close: () => void
    remove: (id: string) => void
  }
  store: Partial<QueueItem>[]
  isProcessing: boolean
}>({
  // @ts-ignore
  actions: {},
  store: [],
  isProcessing: false
})

export const QueueProcessorProvider: React.FC = ({ children }) => {
  const { actions: snackBarActions } = useContext(SimpleSnackbarContext)
  const [store, setStore] = useState<Partial<QueueItem>[]>([])
  const [show, setShow] = useState<boolean>(false)
  const { emitter } = useMitt()

  const { client: socketClient, connected } = useContext(SocketConnectorContext)

  const upsertItem = useCallback(
    (item: Partial<QueueItem>) => {
      setStore(current => {
        const index = current.findIndex(value => value.id === item.id)
        const itemWithTimestamp = {
          ...item,
          updatedAt: new Date().toISOString()
        }

        if (index === -1) {
          return [itemWithTimestamp, ...current]
        }

        return current.map(value => {
          if (value.id === item.id) {
            return {
              ...value,
              ...itemWithTimestamp
            }
          }
          return value
        })
      })
    },
    [setStore]
  )

  const updateExistingItem = useCallback(
    (item: Partial<QueueItem>) => {
      setStore(current => {
        const index = current.findIndex(value => value.id === item.id)
        if (index === -1) {
          return current
        }

        const itemWithTimestamp = {
          ...item,
          updatedAt: new Date().toISOString()
        }

        return current.map(value => {
          if (value.id === item.id) {
            return {
              ...value,
              ...itemWithTimestamp
            }
          }
          return value
        })
      })
    },
    [setStore]
  )

  const open = (target: HTMLElement) => {
    setShow(true)
  }

  const close = () => {
    setShow(false)
  }

  const replace = useCallback(
    async (upload: Partial<QueueItem> & NonNullable<{ id: string; file: File }>) => {
      try {
        const story = await api.changeStoryMedia(upload.id, upload.file, event => {
          const progress = Math.round((event.loaded * 100) / event.total)
          // @ts-ignore
          setStore(value => {
            return value.map(item => {
              if (item.id === upload.id) {
                return {
                  ...item,
                  progress,
                  step: progress < 100 ? 'UPLOADING' : 'UPLOADED'
                }
              }
              return {
                ...item
              }
            })
          })
        })
        setStore(value => {
          return value.map(item => {
            if (item.id === upload.id) {
              return {
                ...item,
                id: story.id,
                progress: undefined,
                step: 'START THE CONVERSION PROCESS'
              }
            }
            return {
              ...item
            }
          })
        })
      } catch (e: any) {
        setStore(value => {
          return value.map(item => {
            if (item.id === upload.id) {
              return {
                ...item,
                progress: 0,
                step: 'ERROR',
                errorDetail: getUploadErrorDetail(e),
                finished: true
              }
            }
            return {
              ...item
            }
          })
        })
      }
    },
    [store, setStore]
  )

  const upload = useCallback(
    async (
      upload: Partial<QueueItem> & NonNullable<{ id: string; file: File; title: string; description: string }>
    ) => {
      try {
        const story = await api.createStory(
          upload.file,
          {
            title: upload.title,
            description: upload.description
          },
          event => {
            const progress = Math.round((event.loaded * 100) / event.total)
            // @ts-ignore
            setStore(value => {
              return value.map(item => {
                if (item.id === upload.id) {
                  return {
                    ...item,
                    progress,
                    step: progress < 100 ? 'UPLOADING' : 'UPLOADED'
                  }
                }
                return {
                  ...item
                }
              })
            })
          }
        )
        setStore(value => {
          return value.map(item => {
            if (item.id === upload.id) {
              return {
                ...item,
                id: story.id,
                progress: undefined,
                step: 'START THE CONVERSION PROCESS'
              }
            }
            return {
              ...item
            }
          })
        })
      } catch (error: any) {
        // @ts-ignore
        const { data } = error.response || {}
        if (data?.code === '1024') {
          snackBarActions.open('User Storage quota exceeded.')
        }
        setStore(value => {
          return value.map(item => {
            if (item.id === upload.id) {
              return {
                ...item,
                progress: 0,
                step: 'ERROR',
                errorDetail: getUploadErrorDetail(error),
                finished: true
              }
            }
            return {
              ...item
            }
          })
        })
      }
    },
    [store, setStore]
  )

  const remove = useCallback(
    (id: string) => {
      setStore(current => current.filter(item => item.id !== id))
    },
    [setStore]
  )

  const addProcessor = useCallback(
    async (item: Partial<QueueItem> & NonNullable<{ id: string }>) => {
      // @ts-ignore
      upsertItem(item)
      setShow(true)
      if (item.step === 'UPLOAD') {
        if (item.action === 'NEW') {
          upload({
            id: item.id,
            file: item.file as File,
            title: item.title as string,
            description: item.description as string
          })
        }

        if (item.action === 'REPLACE') {
          replace({
            id: item.id,
            file: item.file as File,
            title: item.title as string
          })
        }
      }

      await Promise.resolve()
    },
    [replace, upload, upsertItem]
  )

  useEffect(() => {
    connected &&
      socketClient.subscribe('/user/queue/story-media-processing', (message: { body: string }) => {
        const { payload: data } = JSON.parse(message.body)

        const item: QueueItem = {
          id: data.id,
          title: data.title,
          finished: data.ready,
          progress: undefined,
          step: 'PROCESSING'
        }

        updateExistingItem(item)
        emitter.emit('story-media-processing', item)
      })

    connected &&
      socketClient.subscribe('/user/queue/story-media-success', (message: { body: string }) => {
        const { payload: data } = JSON.parse(message.body)

        const item: QueueItem = {
          id: data.id,
          title: data.title,
          finished: data.ready,
          progress: undefined,
          step: 'FINISHED'
        }

        updateExistingItem(item)
        emitter.emit('story-media-success', item)
      })

    connected &&
      socketClient.subscribe('/user/queue/story-media-error', (message: { body: string }) => {
        const { payload: data } = JSON.parse(message.body)

        const item: QueueItem = {
          id: data.id,
          title: data.title,
          finished: data.ready,
          progress: undefined,
          step: 'ERROR'
        }

        updateExistingItem({
          ...item,
          finished: true
        })
        emitter.emit('story-media-error', item)
      })
  }, [socketClient, connected, emitter, updateExistingItem])

  useEffect(() => {
    setShow(store.length > 0)
  }, [store, setShow])

  useEffect(() => {
    const interval = window.setInterval(() => {
      setStore(current =>
        current.map(item => {
          if (item.finished || item.step !== 'PROCESSING' || !item.updatedAt) {
            return item
          }

          const updatedAt = new Date(item.updatedAt).getTime()
          if (Number.isNaN(updatedAt) || Date.now() - updatedAt < PROCESSING_TIMEOUT_MS) {
            return item
          }

          return {
            ...item,
            step: 'ERROR',
            errorDetail: 'Media conversion timed out. Use Reprocess Media on the story page.',
            finished: true
          }
        })
      )
    }, 60_000)

    return () => window.clearInterval(interval)
  }, [setStore])

  useEffect(() => {
    const isUploading = store.some(item => item.step === 'UPLOADING' || item.step === 'UPLOADED')
    if (isUploading) {
      window.onbeforeunload = () => 'Are you sure you want to leave?'
    } else {
      window.onbeforeunload = null
    }
  }, [store])

  return (
    <QueueProcessorContext.Provider
      value={{
        actions: { addProcessor, open, close, remove },
        store,
        isProcessing: store.some(item => !item.finished && item.step !== 'ERROR')
      }}
    >
      {show && <Component />}
      {children}
    </QueueProcessorContext.Provider>
  )
}
